// Small, failure-tolerant wrappers around localStorage (private mode, quota, disabled storage).

import type { CodexAccount, CodexAuthBlob, LangPreference, OpenTab, SummaryLang, TranscriptMode } from '../../shared/api';
import { DEFAULT_LANG_PREFERENCE, isSummaryLang, parseLangPreference } from '../../shared/lang';
import { parseTabs, parseTabOps, type TabOp } from './tabs';

const PREFIX = 'yts:';

export const STORAGE_KEYS = {
  codex: `${PREFIX}codex-auth`,
  settings: `${PREFIX}settings`,
  history: `${PREFIX}history`,
  migrationOffered: `${PREFIX}codex-migration-offered`,
  /** Open tabs of anonymous users. */
  tabs: `${PREFIX}tabs`,
  /** Signed-in users: last known server list plus changes not yet sent (offline). */
  accountTabs: `${PREFIX}tabs-account`,
  /** Summary jobs running on the server, to re-attach after a reload (see jobManager.ts). */
  jobs: `${PREFIX}jobs`,
} as const;

function read(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? null : (JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

// ---------------------------------------------------------------------------
// Codex login of anonymous users (the auth.json blob is opaque to the client).

export interface StoredCodexLogin {
  auth: CodexAuthBlob;
  account?: CodexAccount;
  savedAt: string;
}

export function loadCodexLogin(): StoredCodexLogin | null {
  const value = read(STORAGE_KEYS.codex);
  if (!isObject(value) || !isObject(value.auth)) return null;
  return {
    auth: value.auth,
    ...(isObject(value.account) ? { account: value.account as CodexAccount } : {}),
    savedAt: typeof value.savedAt === 'string' ? value.savedAt : new Date().toISOString(),
  };
}

export function saveCodexLogin(auth: CodexAuthBlob, account?: CodexAccount): StoredCodexLogin {
  const previous = loadCodexLogin();
  const login: StoredCodexLogin = {
    auth,
    ...(account ?? previous?.account ? { account: account ?? previous?.account } : {}),
    savedAt: new Date().toISOString(),
  };
  write(STORAGE_KEYS.codex, login);
  return login;
}

export function clearCodexLogin(): void {
  remove(STORAGE_KEYS.codex);
}

function lastRefresh(auth: CodexAuthBlob | null | undefined): number | undefined {
  const value = auth?.last_refresh;
  if (typeof value !== 'string') return undefined;
  const time = Date.parse(value);
  return Number.isNaN(time) ? undefined : time;
}

/**
 * True when `incoming` is an older version of the login than `stored` (by the auth.json's
 * `last_refresh`). Unknown on either side: not older (the incoming copy is accepted).
 */
export function isOlderCodexAuth(incoming: CodexAuthBlob, stored: CodexAuthBlob | null | undefined): boolean {
  const incomingTime = lastRefresh(incoming);
  const storedTime = lastRefresh(stored);
  return incomingTime !== undefined && storedTime !== undefined && incomingTime < storedTime;
}

// ---------------------------------------------------------------------------
// Summary jobs that run on the server (re-attached after a reload, see jobManager.ts)

export interface PersistedJob {
  videoId: string;
  jobId: string;
  /** When the server job started (client clock): bounds how long it can still exist. */
  jobStartedAt: number;
  /** When the summary was started in the UI (elapsed time). */
  startedAt: number;
  mode?: TranscriptMode;
  requestedLang?: SummaryLang;
  /** Effective language, once the server resolved it. */
  lang?: SummaryLang;
  langPref: LangPreference;
  force: boolean;
  title?: string;
  channel?: string;
}

export function parsePersistedJobs(value: unknown): PersistedJob[] {
  if (!isObject(value)) return [];
  const jobs: PersistedJob[] = [];
  for (const [videoId, item] of Object.entries(value)) {
    if (!isObject(item) || typeof item.jobId !== 'string' || typeof item.jobStartedAt !== 'number') continue;
    const langPref = parseLangPreference(item.langPref);
    if (!langPref) continue;
    jobs.push({
      videoId,
      jobId: item.jobId,
      jobStartedAt: item.jobStartedAt,
      startedAt: typeof item.startedAt === 'number' ? item.startedAt : item.jobStartedAt,
      ...(item.mode === 'client' || item.mode === 'server' ? { mode: item.mode } : {}),
      ...(isSummaryLang(item.requestedLang) ? { requestedLang: item.requestedLang } : {}),
      ...(isSummaryLang(item.lang) ? { lang: item.lang } : {}),
      langPref,
      force: item.force === true,
      ...(typeof item.title === 'string' && item.title ? { title: item.title } : {}),
      ...(typeof item.channel === 'string' && item.channel ? { channel: item.channel } : {}),
    });
  }
  return jobs.sort((a, b) => a.jobStartedAt - b.jobStartedAt);
}

export function loadPersistedJobs(): PersistedJob[] {
  return parsePersistedJobs(read(STORAGE_KEYS.jobs));
}

export function savePersistedJob(job: PersistedJob): void {
  const current = read(STORAGE_KEYS.jobs);
  const { videoId, ...rest } = job;
  write(STORAGE_KEYS.jobs, { ...(isObject(current) ? current : {}), [videoId]: rest });
}

/** Removes the stored job of a video (only if it is `jobId`, when given). */
export function removePersistedJob(videoId: string, jobId?: string): void {
  const current = read(STORAGE_KEYS.jobs);
  if (!isObject(current) || !(videoId in current)) return;
  const entry = current[videoId];
  if (jobId && isObject(entry) && entry.jobId !== jobId) return;
  const next = { ...current };
  delete next[videoId];
  if (Object.keys(next).length) write(STORAGE_KEYS.jobs, next);
  else remove(STORAGE_KEYS.jobs);
}

// ---------------------------------------------------------------------------
// Settings

export interface Settings {
  /** Summary language when the address names none (see src/shared/lang.ts). */
  langPref: LangPreference;
  /** Only used for signed-in users; signed-out users always use client mode. */
  transcriptMode: TranscriptMode;
  /** Notify when a summary is ready while the app is in the background (needs the browser permission). */
  notify: boolean;
  /** Model of new summaries; unset: the account's default. */
  model?: string | undefined;
  /** Reasoning effort of new summaries; unset: the model's default. */
  effort?: string | undefined;
}

/** First-run preference: German and English kept, everything else in German. */
export function defaultLangPreference(): LangPreference {
  return { ...DEFAULT_LANG_PREFERENCE, keep: [...DEFAULT_LANG_PREFERENCE.keep] };
}

/**
 * Reads stored settings. Older versions stored a single `lang` ("de" | "en"): it becomes the
 * fallback, German and English stay kept (as before, nothing changes for German or English videos
 * of users who never touched the setting).
 */
export function parseSettings(value: unknown): Settings {
  const stored = isObject(value) ? value : {};
  const langPref =
    parseLangPreference(stored.langPref) ??
    (isSummaryLang(stored.lang)
      ? { keep: [...DEFAULT_LANG_PREFERENCE.keep], fallback: stored.lang }
      : defaultLangPreference());
  return {
    langPref,
    transcriptMode: stored.transcriptMode === 'client' ? 'client' : 'server',
    notify: stored.notify !== false,
    ...(typeof stored.model === 'string' && stored.model ? { model: stored.model } : {}),
    ...(typeof stored.effort === 'string' && stored.effort ? { effort: stored.effort } : {}),
  };
}

export function loadSettings(): Settings {
  return parseSettings(read(STORAGE_KEYS.settings));
}

export function saveSettings(settings: Settings): void {
  write(STORAGE_KEYS.settings, settings);
}

// ---------------------------------------------------------------------------
// History of recent summaries

export interface HistoryEntry {
  videoId: string;
  lang: SummaryLang;
  title: string;
  channel: string;
  markdown: string;
  model?: string;
  /** When the summary was created (server time). */
  createdAt: string;
  /** When it was last opened here. */
  viewedAt: string;
  durationSeconds?: number;
  /** Base code of the video's spoken language, if known (picks the entry for the language preference). */
  sourceLang?: string;
}

export const HISTORY_LIMIT = 30;

function isHistoryEntry(value: unknown): value is HistoryEntry {
  return (
    isObject(value) &&
    typeof value.videoId === 'string' &&
    isSummaryLang(value.lang) &&
    typeof value.markdown === 'string' &&
    typeof value.title === 'string'
  );
}

export function loadHistory(): HistoryEntry[] {
  const value = read(STORAGE_KEYS.history);
  return Array.isArray(value) ? value.filter(isHistoryEntry).slice(0, HISTORY_LIMIT) : [];
}

/** Saves the list; on quota errors drops the oldest entries until it fits. */
export function saveHistory(entries: HistoryEntry[]): HistoryEntry[] {
  let list = entries.slice(0, HISTORY_LIMIT);
  while (list.length > 0 && !write(STORAGE_KEYS.history, list)) {
    list = list.slice(0, -1);
  }
  if (list.length === 0) remove(STORAGE_KEYS.history);
  return list;
}

// ---------------------------------------------------------------------------
// "Move the browser login to your account?" was offered to this user.

export function wasMigrationOffered(userId: string): boolean {
  const value = read(STORAGE_KEYS.migrationOffered);
  return Array.isArray(value) && value.includes(userId);
}

export function markMigrationOffered(userId: string): void {
  const value = read(STORAGE_KEYS.migrationOffered);
  const list = Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  if (!list.includes(userId)) write(STORAGE_KEYS.migrationOffered, [...list, userId].slice(-20));
}

// ---------------------------------------------------------------------------
// Open tabs (see useOpenTabs.ts)

export function loadLocalTabs(): OpenTab[] {
  return parseTabs(read(STORAGE_KEYS.tabs));
}

export function saveLocalTabs(tabs: OpenTab[]): void {
  if (tabs.length === 0) remove(STORAGE_KEYS.tabs);
  else write(STORAGE_KEYS.tabs, tabs);
}

export interface AccountTabs {
  userId: string;
  /** The list as last answered by the server. */
  tabs: OpenTab[];
  /** Changes not confirmed by the server yet, oldest first. */
  pending: TabOp[];
}

export function loadAccountTabs(): AccountTabs | null {
  const value = read(STORAGE_KEYS.accountTabs);
  if (!isObject(value) || typeof value.userId !== 'string') return null;
  return { userId: value.userId, tabs: parseTabs(value.tabs), pending: parseTabOps(value.pending) };
}

export function saveAccountTabs(value: AccountTabs | null): void {
  if (value) write(STORAGE_KEYS.accountTabs, value);
  else remove(STORAGE_KEYS.accountTabs);
}
